import { create } from 'zustand'
import { calculateBudgetDistribution } from '../utils/ad-campaigns-helpers'

const createBudgetStore = (initialState = {}) =>
    create((set, get) => ({
        // Budget state
        budgetType: initialState.budgetType,
        totalSmartBudget: initialState.totalSmartBudget || '',
        selectedObjective: initialState.selectedObjective || '',
        budgetError: initialState.budgetError || null,
        availableStages: initialState.availableStages || [],
        hasEdits: initialState.hasEdits || false,
        allowedAudiencesByStage: initialState.allowedAudiencesByStage || {},
        manualBudgets: initialState.manualBudgets || {},
        stagesToExclude: initialState.stagesToExclude || [],
        minDailyBudget: initialState.minDailyBudget,

        // Audience selections with stage shares and audience shares
        audienceSelections: initialState.audienceSelections || {},

        // Actions
        setBudgetType: (budgetType) => set({ budgetType }),

        setTotalSmartBudget: (totalSmartBudget) => set({ totalSmartBudget }),

        recalculateBudgetDistribution: () => {
            const state = get()
            const data = {}

            Object.keys(state.audienceSelections).forEach((stage) => {
                data[stage] = Object.keys(state.audienceSelections[stage].audiences)
            })

            const audienceSelections = calculateBudgetDistribution({
                budget: state.totalSmartBudget,
                objective: state.selectedObjective,
                audiencesByStage: data,
                stagesToExclude: state.stagesToExclude,
            })

            set({ audienceSelections })
        },

        setSelectedObjective: (selectedObjective) => set({ selectedObjective }),

        setStageShare: (stageId, share) => {
            const state = get()
            const data = {}

            state.availableStages.forEach((stage) => {
                const stageData = state.audienceSelections[stage]

                if (stageData) data[stage] = Object.keys(stageData.audiences)
            })

            const audienceSelections = calculateBudgetDistribution({
                budget: state.totalSmartBudget,
                objective: state.selectedObjective,
                audiencesByStage: data,
                stagesToExclude: state.stagesToExclude,
                overrides: {
                    [stageId]: share,
                },
            })

            set({ audienceSelections, hasEdits: true })
        },

        setAudienceManualAmount: (stageId, audienceId, amount) => {
            set((state) => {
                const manualBudgets = { ...state.manualBudgets }
                manualBudgets[stageId]['audiences'][audienceId] = { amount }
                return { manualBudgets }
            })
        },

        validateAudienceManualAmount: (stageId, audienceId) => {
            const state = get()
            let currentAmount = state.manualBudgets[stageId]?.audiences?.[audienceId]?.amount || 0

            // Round to 2 decimal places only if it has more than 2 decimal places
            const decimalPlaces = (currentAmount.toString().split('.')[1] || '').length
            if (decimalPlaces > 2) {
                currentAmount = Math.round(currentAmount * 100) / 100
            }

            // Ensure minimum budget
            if (currentAmount < state.minDailyBudget) {
                currentAmount = state.minDailyBudget
            }

            set((state) => {
                const manualBudgets = { ...state.manualBudgets }
                manualBudgets[stageId]['audiences'][audienceId] = { amount: currentAmount }
                return { manualBudgets }
            })
        },

        // Toggle audience selection and recalculate even distribution
        toggleAudience: (stageId, audienceId) => {
            const state = get()
            const data = {}

            // Prepare data object with all stages and their audiences
            // Add or remove audience from stage
            state.availableStages.forEach((stage) => {
                const audienceSelection = state.audienceSelections[stage]

                if (!audienceSelection) return

                const audiences = Object.keys(audienceSelection.audiences)

                if (stage !== stageId) {
                    data[stage] = audiences
                    return
                }

                const hasAudience = audiences.includes(audienceId)

                if (hasAudience) {
                    data[stage] = audiences.filter((audience) => audience !== audienceId)
                } else {
                    data[stage] = [...audiences, audienceId]
                }
            })

            const overrides = state.availableStages
                .filter((stage) => state.audienceSelections[stage])
                .reduce((acc, stage) => {
                    acc[stage] = state.audienceSelections[stage].share
                    return acc
                }, {})

            const audienceSelections = calculateBudgetDistribution({
                budget: state.totalSmartBudget,
                objective: state.selectedObjective,
                audiencesByStage: data,
                overrides,
            })

            set({ audienceSelections })
        },

        // Check if audience is selected
        isAudienceSelected: (stageId, audienceId) => {
            const state = get()
            return !!state.audienceSelections[stageId]?.audiences?.[audienceId]
        },

        // Get audience amount within a stage
        getAudienceAmount: (stageId, audienceId) => {
            const state = get()
            return state.audienceSelections[stageId]?.audiences?.[audienceId]?.amount || 0
        },

        // Get audience amount within a stage
        getAudienceManualAmount: (stageId, audienceId) => {
            const state = get()
            return state.manualBudgets[stageId]?.audiences?.[audienceId]?.amount || 0
        },

        // Get selected audiences for a stage
        getSelectedAudiences: (stageId) => {
            const state = get()
            return Object.keys(state.audienceSelections[stageId]?.audiences || {})
        },

        // Get stage data
        getStageData: (stageId) => {
            const state = get()
            return state.audienceSelections[stageId] || { share: 0, audiences: {} }
        },

        hasAnyAudienceLessBudgetThanAllowed: () => {
            const state = get()

            return state.availableStages.some((stage) => {
                const stageData = state.audienceSelections[stage]

                if (!stageData) return false

                const audiences = Object.values(stageData.audiences)
                return audiences.some((audience) => audience.amount < state.minDailyBudget)
            })
        },

        // Get total budget based on budget type with filtering for allowed stages and audiences
        getTotalBudget: () => {
            const state = get()

            if (state.budgetType === 'smart') return parseFloat(state.totalSmartBudget) || 0

            return state.getTotalBudgetForManualType()
        },

        getTotalBudgetForManualType: () => {
            const state = get()

            // Sum amounts only from allowed stages and audiences
            let totalAmount = 0
            Object.entries(state.manualBudgets).forEach(([stageId, stage]) => {
                // Only include stages that are currently allowed
                if (state.availableStages.length > 0 && !state.availableStages.includes(stageId)) return

                if (stage.audiences) {
                    const allowedAudiencesForStage = state.allowedAudiencesByStage[stageId] || []

                    Object.entries(stage.audiences).forEach(([audienceId, audience]) => {
                        // Only include audiences that are currently allowed for this stage
                        if (allowedAudiencesForStage.length > 0 && !allowedAudiencesForStage.includes(audienceId))
                            return

                        totalAmount += parseFloat(audience.amount) || 0
                    })
                }
            })
            return totalAmount
        },

        // Validate if budget is sufficient
        isBudgetValid: (allowedAudiencesByStage = {}) => {
            const state = get()
            return state.getTotalBudget(allowedAudiencesByStage) > 0
        },

        // Check if budget is low (under $15 * minDailyBudget)
        isBudgetLow: (allowedAudiencesByStage = {}) => {
            const state = get()
            return state.getTotalBudget(allowedAudiencesByStage) < 15 * state.minDailyBudget
        },

        // Clear budget error
        clearBudgetError: () => set({ budgetError: null }),

        // Update stages to exclude and recalculate
        setStagesToExclude: (stagesToExclude) => {
            const state = get()
            const data = {}

            Object.keys(state.audienceSelections).forEach((stage) => {
                data[stage] = Object.keys(state.audienceSelections[stage].audiences)
            })

            const audienceSelections = calculateBudgetDistribution({
                budget: state.totalSmartBudget,
                objective: state.selectedObjective,
                audiencesByStage: data,
                stagesToExclude: stagesToExclude,
            })

            set({ stagesToExclude, audienceSelections })
        },

        // Reset all data
        resetSmartBudgets: () => {
            const state = get()
            const data = {}

            Object.keys(state.audienceSelections).forEach((stage) => {
                data[stage] = Object.keys(state.audienceSelections[stage].audiences)
            })

            const audienceSelections = calculateBudgetDistribution({
                budget: state.totalSmartBudget,
                objective: state.selectedObjective,
                audiencesByStage: data,
                stagesToExclude: state.stagesToExclude,
            })

            set({ audienceSelections, hasEdits: false })
        },
    }))

export default createBudgetStore
